import { createContext, useState, ReactNode, useContext, useEffect, Fragment } from 'react';
import { IUITranslation, TLang } from 'sierra';
import api from '../utils/axios';
import { useMediaQuery, useTheme } from '@mui/material';

interface Languages {
    lang: 'en' | 'ar' | 'zh';
    title: string;
    icon: ReactNode;
}
// Define the type for the context state
interface AppContextType {
    lang: 'en' | 'ar' | 'zh'; // Valid language codes
    setLang: (lang: 'en' | 'ar' | 'zh') => void; // Function to set the language
    languages: Languages[],
    translate: (enText: string) => string,
    screenSize: {
        xs: boolean;
        sm: boolean;
        md: boolean;
        lg: boolean;
    }
}

// Create the context with an initial value
const AppContext = createContext<AppContextType | undefined>(undefined);


// Create a provider component
export const AppProvider = ({ children }: { children: ReactNode }) => {
    const theme = useTheme()

    const [translations, setTranslations] = useState<IUITranslation[]>(JSON.parse(localStorage.getItem(`ui_translations`) || '[]'))
    const [lang, setLang] = useState<'en' | 'ar' | 'zh'>((localStorage.getItem('lang') as TLang) || 'ar'); // Default language is 'en'
    const languages: Languages[] = [
        { lang: 'en', title: 'English', icon: <img width={25} key={0} src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Flag_of_the_United_Kingdom_%281-2%29.svg/800px-Flag_of_the_United_Kingdom_%281-2%29.svg.png" /> },
        {
            lang: 'ar', title: 'العربية', icon:
                <Fragment key={1}>
                    <img width={25} src="https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/Flag_of_Saudi_Arabia.svg/800px-Flag_of_Saudi_Arabia.svg.png" />
                    <img width={25} src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/Flag_of_Egypt.svg/800px-Flag_of_Egypt.svg.png" />
                    <img width={25} src="https://img.freepik.com/premium-photo/united-arab-emirates-flag-wrinkled-dark-background-3d-render_430468-517.jpg" />
                </Fragment>
        },
        { lang: 'zh', title: '中国人', icon: <img width={25} key={2} src='https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Flag_of_the_People%27s_Republic_of_China.svg/2560px-Flag_of_the_People%27s_Republic_of_China.svg.png' /> },
    ]
    const screenSize = {
        xs: useMediaQuery(theme.breakpoints.down('sm')),
        sm: useMediaQuery(theme.breakpoints.down('md')),
        md: useMediaQuery(theme.breakpoints.down('lg')),
        lg: useMediaQuery(theme.breakpoints.down('xl')),
    }

    useEffect(() => {
        api.get('/translations/ui').then(({ data }) => setTranslations(data)).catch(console.error)
    }, [])

    useEffect(() => {
        localStorage.setItem(`ui_translations`, JSON.stringify(translations))
    }, [translations])

    const translate = (enText: string) => {
        return lang == 'en' ? enText : translations.find(obj => obj.en?.toLowerCase() === enText.toLowerCase())?.[lang] || enText
    }

    return (
        <AppContext.Provider value={{ lang, setLang, languages, translate, screenSize }}>
            {children}
        </AppContext.Provider>
    );
};


export const useApp = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within a AppProvider');
    }
    return context;
};